import { Box, Portal } from "@chakra-ui/react";
import React, { memo, useEffect, useRef } from "react";
import { MENTION_TYPE } from "modules/utils/Constants";
import { IClickedMention } from "../use-quill";
import DistributionGroupTooltipContent from "modules/_shared/distribution-group-tooltip-content";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react-dom";

interface IProps {
  clickedMention?: IClickedMention | null;
  onClose: () => void;
}

const QuillClickedMention = ({ clickedMention, onClose }: IProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, isPositioned } = useFloating({
    placement: "top-start",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Position the virtual reference element
  useEffect(() => {
    if (!clickedMention) return;
    refs.setReference({
      getBoundingClientRect: () => ({
        top: clickedMention.top,
        left: clickedMention.left,
        bottom: clickedMention.top + clickedMention.height,
        right: clickedMention.left + clickedMention.width,
        width: clickedMention.width,
        height: clickedMention.height,
        x: clickedMention.left,
        y: clickedMention.top,
        toJSON: () => {},
      }),
    });
  }, [clickedMention, refs]);

  // Close when clicking outside
  useEffect(() => {
    if (!clickedMention) return;
    const handler = (e: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(e.target as Node)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [clickedMention, onClose]);

  if (!clickedMention) return null;

  if (clickedMention.type === MENTION_TYPE.DISTRIBUTION_GROUP) {
    return (
      <Portal>
        <Box
          ref={(node) => {
            contentRef.current = node;
            refs.setFloating(node);
          }}
          style={floatingStyles}
          zIndex="popover"
          bg="white"
          borderRadius={16}
          border="1px solid"
          borderColor="gray.200"
          boxShadow="lg"
          p={3}
          w={300}
          maxH={600}
          overflowY="auto"
          visibility={isPositioned ? "visible" : "hidden"}
        >
          <DistributionGroupTooltipContent id={clickedMention.id} />
        </Box>
      </Portal>
    );
  }

  return null;
};

export default memo(QuillClickedMention);
